import React, { useContext, useEffect, useState, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import axios from "axios";
import { Link, navigate, useQueryParams } from 'raviger';
import { Button, Card, CardGroup, Col, Collapse, Form, FormControl, InputGroup, ListGroup, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan, faCalendarDay, faInfoCircle, faClipboardList, faCut, faEnvelope, faLink, faMapMarkerAlt, faMedkit, faUserAltSlash
} from '@fortawesome/free-solid-svg-icons';
import {
  faDotCircle
} from '@fortawesome/free-regular-svg-icons';
import { faHandsHolding, faChevronDoubleDown, faChevronDoubleUp, faClawMarks, faFolderMedical, faPhoneRotary } from '@fortawesome/pro-solid-svg-icons';
import Moment from 'react-moment';
import Select, { components } from 'react-select';
import L from "leaflet";
import { Circle, MapContainer, Marker, Tooltip as MapTooltip, ScaleControl, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { useMark, useSubmitting, useDataImg, useLocationWithRoutes } from '../hooks';
import Header from '../components/Header';
import Scrollbar from '../components/Scrollbars';
import { titleCase } from '../components/Utils';
import { ITEMS_PER_PAGE } from '../constants';
import { catColorChoices, dogColorChoices, horseColorChoices, otherColorChoices, statusChoices } from './constants';
import AnimalCoverImage from '../components/AnimalCoverImage';
import { printAnimalCareSchedule, printAllAnimalCareSchedules } from './Utils';
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';
import ShelterlyPrintifyButton from '../components/ShelterlyPrintifyButton';
import ActionsDropdown from '../components/ActionsDropdown';
import Paginator from '../components/Paginator';
import '../assets/styles.css';

const NoOptionsMessage = props => {
  return (
    <components.NoOptionsMessage {...props}>
      <span>Select a species</span>
    </components.NoOptionsMessage>
  );
};

function AnimalSearch() {

  const { dispatch, state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  // Identify any query param data.
  const [queryParams] = useQueryParams();
  const {
    incident = '',
    organization = '',
  } = queryParams;

  const fixedChoices = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: 'Unknown'}
  ];

  const sexChoices = [
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
  ];

  const [data, setData] = useState({animals: [], total_count: 0, isFetching: false});
  const [speciesChoices, setSpeciesChoices] = useState([]);
  const [options, setOptions] = useState({id:null, species:'', sex:null, pcolor:'', fixed:null});
  const [showFilters, setShowFilters] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [incidentSlug, setIncidentSlug] = useState(incident);
  const [incidentOptions, setIncidentOptions] = useState([]);
  const [organizationSlug, setOrganizationSlug] = useState(organization);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const speciesRef = useRef(null);
  const sexRef = useRef(null);
  const fixedRef = useRef(null);
  const pcolorRef = useRef(null);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const { promiseImage, getBase64Image } = useDataImg();
  const [lazyAnimalImages, setLazyAnimalImages] = useState([]);

  function findLazyAnimalImage (animalId) {
    return lazyAnimalImages.find((lazyAnimal) => lazyAnimal.id === animalId);
  }
  function addLazyAnimalImage (animalId, animalImage) {
    setLazyAnimalImages((prevState) => {
      prevState.push({
        id: animalId,
        image: animalImage
      });
      return prevState;
    })
  }

  const colorChoices = {'':[], 'dog':dogColorChoices, 'cat':catColorChoices, 'horse':horseColorChoices, 'other':otherColorChoices}

  const handleSubmit = async event => {
    event.preventDefault();
    // setTriggerRefresh(!triggerRefresh);
    setPage(1);
  };

  const handleShowFilters = () => {
    setShowFilters(!showFilters);
    // const map = useMap();
    // setTimeout(() => {
    //   mapRef.current.leafletElement.invalidateSize();
    // }, 1000);
  };

  const handleClear = () => {
    speciesRef.current.clearValue();
    sexRef.current.clearValue();
    fixedRef.current.clearValue();
    pcolorRef.current.clearValue();
    setOptions({id:null, species:'', sex:null, owned:null, pcolor:'', fixed:null});
    setTriggerRefresh(!triggerRefresh);
  };

  const customStyles = {
    // For the select it self, not the options of the select
    control: (styles, { isDisabled}) => {
      return {
        ...styles,
        color: '#FFF',
        cursor: isDisabled ? 'not-allowed' : 'default',
        backgroundColor: isDisabled ? '#DFDDDD' : 'white',
        height: 35,
        minHeight: 35,
        marginBottom: "15px",
      }
    },
    option: provided => ({
      ...provided,
      color: 'black',
    }),
    menu: provided  => ({ ...provided, zIndex: 9999 })
  };

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchSpecies = () => {
      setSpeciesChoices([]);
      // Fetch Species data.
      axios.get('/animals/api/species/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let species_options = [];
          response.data.forEach(result => {
            // Build species option list.
            species_options.push({value: result.name, label: result.name});
          });
          setSpeciesChoices(species_options);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    fetchSpecies();

    const fetchAllOrganizationData = async () => {
      // Fetch Organization data.
      await axios.get('/incident/api/public/organizations/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let options = [];
          response.data.forEach(organization => {
            // Build organization option list.
            options.push({value:organization.id, label:organization.name, slug:organization.slug});
          });
          setOrganizationOptions(options);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    if (organization) {
      axios.get('/incident/api/public/organizations/' + organization + '/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          if (response.data) {
            setOrganizationOptions([{value:response.data.id, label:response.data.name, slug:response.data.slug}]);
          }
          else {
            setOrganizationOptions([]);
          }
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    }
    else {
      fetchAllOrganizationData();
    }

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, []);

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchAllIncidentData = async () => {
      // Fetch Incident data.
      await axios.get('/incident/api/public/incidents/?organization_slug=' + organizationSlug, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let options = [];
          response.data.forEach(inc => {
            // Build incident option list.
            options.push({
              value: inc.id,
              label: inc.name,
              slug: inc.slug,
            });
          });
          setIncidentOptions(options);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    if (incident) {
      axios.get('/incident/api/public/incidents/' + incident + '/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          if (response.data) {
            setIncidentOptions([{value:response.data.id, label:response.data.name, slug:response.data.slug}]);
          }
          else {
            setIncidentOptions([]);
          }
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    }
    else if (organizationSlug) {
      fetchAllIncidentData();
    }

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [organizationSlug]);

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchAnimals = async () => {
      setIsLoading(true);
      setNumPages(0);
      setData({animals: [], total_count: 0, isFetching: true});
      // Fetch Animal data.
      await axios.get('/animals/api/public/?page=' + page +'&incident=' + incidentSlug + '&organization=' + organizationSlug, {
        cancelToken: source.token,
        params: {
          species: options.species,
          sex: options.sex,
          fixed: options.fixed,
          pcolor: options.pcolor,
          scolor: options.scolor,
        },
      })
      .then(async (response) => {
        if (!unmounted) {
          setNumPages(Math.ceil(response.data.count / ITEMS_PER_PAGE));
          let animals = response.data.results;
          setData({animals: animals, total_count: response.data.count, isFetching: false});
          setIsLoading(false);
          for (const animal of animals) {
            // lazy load animal front_image
            let lazyImage = findLazyAnimalImage(animal.id)?.image;
            if (!lazyImage && animal.front_image) {
              lazyImage = await promiseImage(animal.front_image);
              addLazyAnimalImage(animal.id, lazyImage);
            }
            if (unmounted) return;
            if (lazyImage) {
              // Update state immutably so the list re-renders with the loaded image.
              setData(prev => ({
                ...prev,
                animals: prev.animals.map(a => a.id === animal.id ? {...a, lazyImage} : a),
              }));
            }
          }
        }
      })
      .catch(error => {
        if (!unmounted) {
          setData({animals: [], total_count: 0, isFetching: false});
          setShowSystemError(true);
        }
      });
    };
    if (organizationSlug) {
      fetchAnimals();
    }

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [incidentSlug, organizationSlug, triggerRefresh, page]);

  // Hook handling option changes.
  useEffect(() => {
    setIsDisabled(!(options.id || options.species || options.status || options.sex || options.owned || options.pcolor || options.fixed || options.latlng || options.shelter));
  }, [options]);

  return (
    <div className="ms-auto me-auto public-search-container">
      <Header>Public Animal Search</Header>
      <Form onSubmit={handleSubmit}>
        {organizationOptions.length ?
        <Row className="gx-2">
          <Col xs={12} md>
            <label className="mb-2">Organization</label>
            {organizationOptions.length ? <Select
              label="Organization"
              id="organization"
              name="organization"
              type="text"
              placeholder="Select Organization..."
              options={organizationOptions}
              styles={customStyles}
              defaultValue={organization ? organizationOptions[0] : null}
              onChange={(instance) => {
                setOrganizationSlug(instance ? instance.slug : '');
              }}
              isDisabled={isLoading ||  organization ? true : false}
            /> : ""}
          </Col>
          <Col xs={12} md>
            <label className="mb-2">Incident</label>
            {organizationOptions.length && incidentOptions.length ? <Select
              label="Incident"
              id="incident"
              name="incident"
              type="text"
              placeholder={organizationSlug ? "Select Incident..." : "Select Organization..."}
              options={incidentOptions}
              styles={customStyles}
              isClearable={true}
              defaultValue={incident ? incidentOptions[0] : null}
              onChange={(instance) => {
                setIncidentSlug(instance ? instance.slug : '');
              }}
              isDisabled={isLoading || incident ? true : false}
            /> :
            ""}
            {organizationOptions.length && !incidentOptions.length ? <Select
              id="filler"
              name="filler"
              type="text"
              placeholder={organizationSlug ? "Select Incident..." : "Select Organization..."}
              options={[]}
              styles={customStyles}
              // defaultValue={incident ? incidentOptions[0] : null}
              // onChange={(instance) => {
              //   setIncidentSlug(instance ? instance.slug : '');
              // }}
              isDisabled={isLoading || incident ? true : false}
            /> :
            ""}
          </Col>
          <Col xs={12} md>
            <Button variant="outline-light" className="rounded w-100 public-search-filters-btn" style={{color:"white"}} onClick={handleShowFilters}>Filters {showFilters ? <FontAwesomeIcon icon={faChevronDoubleUp} className="fa-move-up" size="sm" /> : <FontAwesomeIcon icon={faChevronDoubleDown} className="fa-move-up" size="sm" />}</Button>
          </Col>
        </Row> :
        <Row>
          <Col>
            <Card.Header>
              No public data available.
            </Card.Header>
          </Col>
        </Row>}
        <Collapse in={showFilters}>
        <div>
          <Card className="border rounded d-flex mb-3" style={{width:"100%"}}>
            <Card.Body>
              <Row style={{marginBottom:"-15px"}}>
                <Col xs={12} md={4} style={{textTransform:"capitalize"}}>
                  <Select
                    label="Species"
                    id="speciesDropdown"
                    name="species"
                    type="text"
                    placeholder="Select Species"
                    options={speciesChoices}
                    styles={customStyles}
                    isClearable={true}
                    ref={speciesRef}
                    onChange={(instance) => {
                      pcolorRef.current.clearValue();
                      setOptions({...options, species: instance ? instance.value : null, pcolor: ''});
                    }}
                  />
                  <Select
                    label="Sex"
                    id="sexDropdown"
                    name="sex"
                    type="text"
                    placeholder="Select Sex"
                    options={sexChoices}
                    styles={customStyles}
                    isClearable={true}
                    ref={sexRef}
                    onChange={(instance) => {
                      setOptions({...options, sex: instance ? instance.value : null})
                    }}
                  />
                  <Select
                    label="Fixed"
                    id="fixedDropdown"
                    name="fixed"
                    type="text"
                    placeholder="Select Altered"
                    options={fixedChoices}
                    styles={customStyles}
                    isClearable={true}
                    ref={fixedRef}
                    onChange={(instance) => {
                      setOptions({...options, fixed: instance ? instance.value : null})
                    }}
                  />
                  <Select
                    label="Primary Color"
                    id="pcolorDropdown"
                    name="pcolor"
                    type="text"
                    placeholder="Select Color"
                    components={{ NoOptionsMessage }}
                    ref={pcolorRef}
                    options={Object.keys(colorChoices).includes(options.species) ? colorChoices[options.species] : colorChoices['other']}
                    styles={customStyles}
                    isClearable={true}
                    onChange={(instance) => {
                      setOptions({...options, pcolor: instance ? instance.value : ''})
                    }}
                  />
                </Col>
                <Col className="ps-md-0 pb-3 pb-md-0" xs={12} md={3}>
                  <div><Button className="btn btn-primary w-100" style={{maxHeight:"35px"}} onClick={() => {setTriggerRefresh(!triggerRefresh);}} disabled={isDisabled}>Apply</Button></div>
                  <Button variant="outline-light w-100" style={{maxHeight:"35px", marginTop:"15px"}} onClick={handleClear}>Clear</Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
        </Collapse>
      </Form>
      {data.animals.map((animal, index) => (
        <div key={animal.id} className="border rounded mb-3">
          <div className="card-header rounded">
            <h4 style={{marginBottom:"-2px",  marginLeft:"-12px"}}>
              <OverlayTrigger
                key={"animal-details"}
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-animal-details`}>
                    Animal details
                  </Tooltip>
                }
              >
                <Link href={"/public?id=" + animal.id}><FontAwesomeIcon icon={faDotCircle} className="me-2" inverse /></Link>
              </OverlayTrigger>
              A#{animal.id_for_incident}
            </h4>
          </div>
          <CardGroup className="public-animal-cards" style={{marginBottom:"-6px"}}>
            <Card className="public-animal-image">
              <Card.Body className="p-0 m-0 d-flex justify-content-center align-items-center">
                {animal.front_image && !animal.lazyImage
                  ? (
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading Image...</span>
                    </Spinner>
                  )
                : (
                  <AnimalCoverImage
                    width="100%"
                    animalSpecies={animal.species_string_singular}
                    animalImageSrc={
                      animal.lazyImage
                        ? `data:image/png;base64,${getBase64Image(animal.lazyImage)}`
                        : animal.front_image
                    }
                  />
                )}
              </Card.Body>
            </Card>
            <Card className="public-animal-info" style={{marginBottom:"6px"}}>
              <Card.Body>
                <Card.Title style={{marginTop:"-9px", marginBottom:"8px"}}>Information</Card.Title>
                <Scrollbar style={{height:"159px", marginBottom:"-16px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                  <ListGroup>
                    <ListGroup.Item>Species: {titleCase(animal.species_string)}</ListGroup.Item>
                    <ListGroup.Item>Count: {animal.animal_count}</ListGroup.Item>
                    <ListGroup.Item>Incident: {titleCase(animal.incident_name)}</ListGroup.Item>
                  </ListGroup>
                </Scrollbar>
              </Card.Body>
            </Card>
            <Card style={{marginBottom:"6px"}}>
              <Card.Body className="pt-0 pt-md-3">
              <Card.Title className="d-none d-md-block" style={{marginTop:"-9px", marginBottom:"8px"}}>&nbsp;</Card.Title>
                <ListGroup>
                  <ListGroup.Item>Sex: {animal.sex || "N/A"}</ListGroup.Item>
                  <ListGroup.Item>Altered/Neutered: {titleCase(animal.fixed) || "N/A"}</ListGroup.Item>
                  <ListGroup.Item>Color: {!animal.pcolor && !animal.scolor ? "N/A" : ""}{titleCase(animal.pcolor)}{animal.scolor ? ((animal.pcolor && animal.scolor ? " / " : "") + titleCase(animal.scolor)) : ""}</ListGroup.Item>
                  {/* <ListGroup.Item>Secondary Color: {titleCase(animal.scolor) || "N/A"}</ListGroup.Item> */}
                </ListGroup>
              </Card.Body>
            </Card>
          </CardGroup>
        </div>
      ))}
      {organizationSlug ? <p>{data.isFetching && organizationOptions.length ? 'Fetching Animals...' : <span>{data.animals.length === 0 ? 'No animals found.' : ''}</span>}</p> : ""}
      {organizationSlug ? <Paginator page={page} numPages={numPages} setPage={setPage} /> : ""}
    </div>
  )
}

export default AnimalSearch;
